import type { ExtractionJob } from '@ctw/shared/api/ai/use-extraction-jobs';
import type { SelectOption } from '@ctw/shared/components/form/select-input';

// Sentinel value the extraction API uses for fields it couldn't populate.
const NOT_DOCUMENTED = 'not documented';

export interface MappedFact {
  id: string;
  label: string;
  value: string;
  isDocumented: boolean;
  citationIds: Array<number>;
}

export interface MappedFactGroup {
  key: string;
  label: string;
  facts: Array<MappedFact>;
}

export interface MappedExtraction {
  groups: Array<MappedFactGroup>;
  fieldsTotal: number;
  fieldsExtracted: number;
  createdAt?: Date;
  specimenId?: string;
}

const isDocumented = (value: string) => value.trim().toLowerCase() !== NOT_DOCUMENTED;

const getSpecimenId = (job: ExtractionJob): string | undefined => {
  // TEMPUS SPECIFIC - we may want to generalize this
  const value = job.attributes.groups?.specimen?.values?.specimenId?.value;
  return value || undefined;
};

export function mapExtractionJob(job: ExtractionJob): MappedExtraction {
  let fieldsTotal = 0;
  let fieldsExtracted = 0;

  const groups: Array<MappedFactGroup> = Object.entries(job.attributes.groups ?? {}).map(
    ([groupKey, group]) => ({
      key: groupKey,
      label: group.display,
      facts: Object.entries(group.values).map(([fieldKey, field]) => {
        fieldsTotal += 1;
        const documented = isDocumented(field.value);
        if (documented) {
          fieldsExtracted += 1;
        }
        return {
          id: `${groupKey}.${fieldKey}`,
          label: field.display,
          value: field.value,
          isDocumented: documented,
          citationIds: field.citationIds,
        };
      }),
    }),
  );

  return {
    groups,
    fieldsTotal,
    fieldsExtracted,
    createdAt: job.attributes.createdAt ? new Date(job.attributes.createdAt) : undefined,
    specimenId: getSpecimenId(job),
  };
}

export function buildReportOptions(jobs: Array<ExtractionJob>): Array<SelectOption> {
  return jobs.map((job, index) => {
    const dateTime = job.attributes.createdAt
      ? new Date(job.attributes.createdAt).toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        })
      : undefined;
    const name = job.config?.name ?? `Extraction ${index + 1}`;
    return {
      label: dateTime ? `${name} • ${dateTime}` : name,
      value: job.id,
    };
  });
}
